import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  InputAdornment,
  Grid,
  Skeleton,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Print,
  CheckCircle,
  History,
  Inventory2,
  TrendingDown,
  TrendingUp,
  Warning,
  Undo,
} from '@mui/icons-material';
import { getInventoryItems } from '../firebase/inventoryService';
import { processStocktake, getAdjustmentHistory, reverseStockAdjustment } from '../firebase/stockAdjustmentService';

const StockAdjustment = ({ currentUser, permissions }) => {
  const [tab, setTab] = useState(0);
  const [inventory, setInventory] = useState([]);
  const [adjustments, setAdjustments] = useState({}); // { itemId: physicalCount }
  const [reason, setReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reverseConfirmOpen, setReverseConfirmOpen] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [reversing, setReversing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isAdmin = permissions?.userType === 'admin';
  const canViewProfitLoss = isAdmin || permissions?.viewProfitLoss === true;

  const loadInventory = useCallback(async () => {
    setLoading(true);
    const result = await getInventoryItems();
    if (result.success) {
      setInventory(result.data);
      // Pre-fill physical counts with system counts
      const initial = {};
      result.data.forEach(item => {
        initial[item.id] = item.stock;
      });
      setAdjustments(initial);
    } else {
      showSnackbar(result.error || 'Failed to load inventory', 'error');
    }
    setLoading(false);
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    const result = await getAdjustmentHistory();
    if (result.success) {
      setHistory(result.data);
    }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  useEffect(() => {
    if (tab === 1) loadHistory();
  }, [tab, loadHistory]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handlePhysicalCountChange = (itemId, value) => {
    const parsed = value === '' ? '' : Math.max(0, parseInt(value, 10) || 0);
    setAdjustments(prev => ({ ...prev, [itemId]: parsed }));
  };

  const getVariance = (item) => {
    const physical = adjustments[item.id];
    if (physical === '' || physical === undefined) return 0;
    return Number(physical) - item.stock;
  };

  // Calculate loss value using FIFO batch costs — same logic as the service
  // so the UI preview matches what actually gets recorded as an expense
  const getLossValue = (item) => {
    const variance = getVariance(item);
    if (variance >= 0) return 0;

    const batches = [...(item.batches || [])]
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    if (batches.length === 0) {
      // No batch data — fall back to average cost
      return Math.abs(variance) * (item.averageCost || item.costPrice || 0);
    }

    let remaining = Math.abs(variance);
    let loss = 0;
    for (const batch of batches) {
      if (remaining <= 0) break;
      const deduct = Math.min(batch.quantityRemaining || 0, remaining);
      loss += deduct * (batch.costPrice || 0);
      remaining -= deduct;
    }
    // If shortage exceeds all batch quantities (data inconsistency), cover remainder with average cost
    if (remaining > 0) {
      loss += remaining * (item.averageCost || item.costPrice || 0);
    }
    return loss;
  };

  const changedItems = inventory.filter(item => {
    const physical = adjustments[item.id];
    return physical !== '' && physical !== undefined && Number(physical) !== item.stock;
  });

  const totalLossValue = changedItems.reduce((sum, item) => sum + getLossValue(item), 0);
  const shortageCount = changedItems.filter(item => getVariance(item) < 0).length;
  const surplusCount = changedItems.filter(item => getVariance(item) > 0).length;

  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.category || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.itemCode && item.itemCode.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleProcessClick = () => {
    if (!reason.trim()) {
      showSnackbar('Please enter a reason / reference (e.g. Q1 2026 Physical Count)', 'warning');
      return;
    }
    if (changedItems.length === 0) {
      showSnackbar('No adjustments to process — all physical counts match system counts', 'info');
      return;
    }
    setConfirmOpen(true);
  };

  const handleConfirmProcess = async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const adjList = changedItems.map(item => ({
        itemId: item.id,
        itemName: item.name,
        itemCode: item.itemCode || '',
        category: item.category || '',
        systemCount: item.stock,
        physicalCount: Number(adjustments[item.id]),
      }));

      const result = await processStocktake(adjList, reason, currentUser);
      if (result.success) {
        showSnackbar(
          `Stocktake processed: ${result.results.length} item(s) adjusted. ${shortageCount > 0 ? `${shortageCount} shortage(s) recorded as stock loss.` : ''}`,
          'success'
        );
        await loadInventory();
        setReason('');
      } else {
        showSnackbar(result.error || 'Failed to process stocktake', 'error');
      }
    } catch (err) {
      showSnackbar('Unexpected error: ' + err.message, 'error');
    }
    setSubmitting(false);
  };

  const handleReverseClick = (adjustment) => {
    setSelectedAdjustment(adjustment);
    setReverseConfirmOpen(true);
  };

  const handleConfirmReverse = async () => {
    if (!selectedAdjustment) return;
    
    setReverseConfirmOpen(false);
    setReversing(true);
    
    try {
      const result = await reverseStockAdjustment(selectedAdjustment.id, currentUser);
      if (result.success) {
        showSnackbar('Stock adjustment reversed successfully. Inventory and expense records have been updated.', 'success');
        await loadHistory();
        await loadInventory();
      } else {
        showSnackbar(result.error || 'Failed to reverse adjustment', 'error');
      }
    } catch (err) {
      showSnackbar('Unexpected error: ' + err.message, 'error');
    }
    
    setReversing(false);
    setSelectedAdjustment(null);
  };

  const handlePrint = () => {
    const printItems = filteredInventory.map(item => ({
      name: item.name,
      itemCode: item.itemCode || '',
      category: item.category || '',
      system: item.stock,
      physical: adjustments[item.id] ?? item.stock,
      variance: getVariance(item),
      lossValue: getLossValue(item),
    }));

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Stocktake Report</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; color: #000; margin: 20px; }
          h1 { font-size: 18px; margin-bottom: 4px; }
          .meta { font-size: 11px; color: #555; margin-bottom: 16px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
          th { background: #f0f0f0; font-weight: bold; }
          .shortage { color: #c00; font-weight: bold; }
          .surplus { color: #060; font-weight: bold; }
          .summary { margin-top: 16px; font-size: 12px; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>Stocktake Report</h1>
        <div class="meta">
          Date: ${new Date().toLocaleDateString('en-LK')} &nbsp;|&nbsp;
          Reference: ${reason || '—'} &nbsp;|&nbsp;
          Prepared by: ${currentUser?.email || '—'}
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th>Item Code</th>
              <th>Category</th>
              <th>System Count</th>
              <th>Physical Count</th>
              <th>Variance</th>
              ${canViewProfitLoss ? '<th>Loss Value (LKR)</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${printItems.map((item, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${item.name}</td>
                <td>${item.itemCode || '—'}</td>
                <td>${item.category}</td>
                <td>${item.system}</td>
                <td>${item.physical}</td>
                <td class="${item.variance < 0 ? 'shortage' : item.variance > 0 ? 'surplus' : ''}">
                  ${item.variance > 0 ? '+' : ''}${item.variance}
                </td>
                ${canViewProfitLoss ? `<td>${item.lossValue > 0 ? item.lossValue.toFixed(2) : '—'}</td>` : ''}
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="summary">
          <strong>Summary:</strong>
          Total items: ${printItems.length} &nbsp;|&nbsp;
          Shortages: ${printItems.filter(i => i.variance < 0).length} &nbsp;|&nbsp;
          Surpluses: ${printItems.filter(i => i.variance > 0).length}
          ${canViewProfitLoss ? ` &nbsp;|&nbsp; Total Loss Value: LKR ${totalLossValue.toFixed(2)}` : ''}
        </div>
        <div style="margin-top:40px; display:flex; justify-content:space-between;">
          <div>Counted by: ___________________</div>
          <div>Verified by: ___________________</div>
          <div>Date: ___________________</div>
        </div>
      </body>
      </html>
    `;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  };

  const StatCard = ({ icon, value, label, color }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
            {icon}
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={700} color={color || 'text.primary'}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={300} height={50} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={400} height={30} sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map(i => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card><CardContent><Skeleton variant="rectangular" height={80} /></CardContent></Card>
            </Grid>
          ))}
        </Grid>
        <Card><CardContent><Skeleton variant="rectangular" height={400} /></CardContent></Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Inventory2 sx={{ color: 'text.primary' }} />
            <Typography variant="h4" fontWeight={700}>
              Stock Adjustment
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Quarterly physical inventory count and manual stock adjustments
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrint}
            size="small"
          >
            Print Stocktake Sheet
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Stocktake" icon={<Inventory2 fontSize="small" />} iconPosition="start" />
        <Tab label="Adjustment History" icon={<History fontSize="small" />} iconPosition="start" />
      </Tabs>

      {/* ── STOCKTAKE TAB ── */}
      {tab === 0 && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Inventory2 />}
                value={inventory.length}
                label="Total Items"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<Warning />}
                value={changedItems.length}
                label="Items with Variance"
                color={changedItems.length > 0 ? 'warning.main' : 'text.primary'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                icon={<TrendingDown />}
                value={shortageCount}
                label="Shortages"
                color={shortageCount > 0 ? 'error.main' : 'text.primary'}
              />
            </Grid>
            {canViewProfitLoss ? (
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<TrendingDown />}
                  value={`LKR ${totalLossValue.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  label="Estimated Loss Value"
                  color={totalLossValue > 0 ? 'error.main' : 'text.primary'}
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={<TrendingUp />}
                  value={surplusCount}
                  label="Surpluses"
                  color={surplusCount > 0 ? 'success.main' : 'text.primary'}
                />
              </Grid>
            )}
          </Grid>

          {/* Reason + Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    label="Reason / Reference *"
                    placeholder="e.g. Q1 2026 Physical Count, Annual Stocktake"
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    helperText="This will be recorded against all adjustments in this session"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
                    onClick={handleProcessClick}
                    disabled={submitting}
                    sx={{ height: 56 }}
                  >
                    {submitting ? 'Processing...' : `Process Adjustments${changedItems.length > 0 ? ` (${changedItems.length})` : ''}`}
                  </Button>
                </Grid>
              </Grid>
              {changedItems.length > 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>{changedItems.length} item(s)</strong> will be adjusted —{' '}
                  {shortageCount > 0 && <span>{shortageCount} shortage(s) will create stock loss expense records. </span>}
                  {surplusCount > 0 && <span>{surplusCount} surplus(es) will add stock to the newest batch.</span>}
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Inventory Table */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Inventory Items
                </Typography>
                <TextField
                  size="small"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  sx={{ width: 260 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="center"><strong>System Count</strong></TableCell>
                      <TableCell align="center" sx={{ minWidth: 130 }}><strong>Physical Count</strong></TableCell>
                      <TableCell align="center"><strong>Variance</strong></TableCell>
                      {canViewProfitLoss && <TableCell align="right"><strong>Loss Value (LKR)</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredInventory.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={canViewProfitLoss ? 6 : 5} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No items found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {filteredInventory.map(item => {
                      const variance = getVariance(item);
                      const lossVal = getLossValue(item);
                      const hasChange = Number(adjustments[item.id]) !== item.stock && adjustments[item.id] !== '';

                      return (
                        <TableRow
                          key={item.id}
                          sx={{
                            backgroundColor: hasChange
                              ? variance < 0
                                ? 'rgba(211,47,47,0.06)'
                                : 'rgba(46,125,50,0.06)'
                              : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {item.name}
                            </Typography>
                            {item.itemCode && (
                              <Typography variant="caption" color="primary.main">
                                {item.itemCode}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {item.category || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={item.trackInML ? `${item.stock} ml` : item.stock}
                              size="small"
                              color={item.stock > 20 ? 'success' : item.stock > 0 ? 'warning' : 'error'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <TextField
                              type="number"
                              size="small"
                              value={adjustments[item.id] ?? item.stock}
                              onChange={e => handlePhysicalCountChange(item.id, e.target.value)}
                              inputProps={{ min: 0, style: { textAlign: 'center', width: 70 } }}
                              sx={{ width: 100 }}
                              helperText={item.trackInML ? 'ml' : ''}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {variance === 0 ? (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            ) : (
                              <Tooltip title={variance < 0 ? 'Shortage' : 'Surplus'}>
                                <Chip
                                  label={`${variance > 0 ? '+' : ''}${variance}${item.trackInML ? 'ml' : ''}`}
                                  size="small"
                                  color={variance < 0 ? 'error' : 'success'}
                                  icon={variance < 0 ? <TrendingDown fontSize="small" /> : <TrendingUp fontSize="small" />}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                          {canViewProfitLoss && (
                            <TableCell align="right">
                              {lossVal > 0 ? (
                                <Typography variant="body2" color="error.main" fontWeight={600}>
                                  {lossVal.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── HISTORY TAB ── */}
      {tab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Adjustment History
            </Typography>
            {historyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell><strong>Category</strong></TableCell>
                      <TableCell align="center"><strong>System</strong></TableCell>
                      <TableCell align="center"><strong>Physical</strong></TableCell>
                      <TableCell align="center"><strong>Variance</strong></TableCell>
                      {canViewProfitLoss && <TableCell align="right"><strong>Loss Value (LKR)</strong></TableCell>}
                      <TableCell><strong>Reason</strong></TableCell>
                      <TableCell><strong>Adjusted By</strong></TableCell>
                      <TableCell align="center"><strong>Status</strong></TableCell>
                      {isAdmin && <TableCell align="center"><strong>Actions</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={isAdmin ? (canViewProfitLoss ? 11 : 10) : (canViewProfitLoss ? 10 : 9)} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">No adjustment history found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {history.map(record => (
                      <TableRow key={record.id} hover sx={{ opacity: record.reversed ? 0.6 : 1 }}>
                        <TableCell>
                          <Typography variant="body2">{record.date || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{record.itemName}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{record.category || '—'}</Typography>
                        </TableCell>
                        <TableCell align="center">{record.systemCount}</TableCell>
                        <TableCell align="center">{record.physicalCount}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${record.variance > 0 ? '+' : ''}${record.variance}`}
                            size="small"
                            color={record.variance < 0 ? 'error' : record.variance > 0 ? 'success' : 'default'}
                          />
                        </TableCell>
                        {canViewProfitLoss && (
                          <TableCell align="right">
                            {record.lossValue > 0 ? (
                              <Typography variant="body2" color="error.main" fontWeight={600}>
                                {record.lossValue.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{record.reason || '—'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">{record.adjustedByName || '—'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          {record.reversed ? (
                            <Chip
                              label="Reversed"
                              size="small"
                              color="default"
                              icon={<Undo fontSize="small" />}
                            />
                          ) : (
                            <Chip
                              label="Active"
                              size="small"
                              color="success"
                            />
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell align="center">
                            {!record.reversed ? (
                              <Tooltip title="Reverse this adjustment">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => handleReverseClick(record)}
                                  disabled={reversing}
                                >
                                  <Undo fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            <Typography variant="h6">Confirm Stock Adjustment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will permanently update inventory stock levels and cannot be undone.
          </Alert>
          <Typography variant="body2" gutterBottom>
            <strong>Reference:</strong> {reason}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Items to adjust:</strong> {changedItems.length}
          </Typography>
          {shortageCount > 0 && (
            <Typography variant="body2" color="error.main" gutterBottom>
              <strong>Shortages:</strong> {shortageCount} item(s) — stock loss expense records will be created automatically
            </Typography>
          )}
          {surplusCount > 0 && (
            <Typography variant="body2" color="success.main" gutterBottom>
              <strong>Surpluses:</strong> {surplusCount} item(s) — stock will be added to the newest batch
            </Typography>
          )}
          {canViewProfitLoss && totalLossValue > 0 && (
            <Typography variant="body2" color="error.main">
              <strong>Total estimated loss:</strong> LKR {totalLossValue.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmProcess}
            startIcon={<CheckCircle />}
          >
            Confirm & Process
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reverse Confirmation Dialog */}
      <Dialog open={reverseConfirmOpen} onClose={() => setReverseConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Undo color="warning" />
            <Typography variant="h6">Reverse Stock Adjustment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will reverse the stock adjustment and restore inventory to the original system count.
          </Alert>
          {selectedAdjustment && (
            <>
              <Typography variant="body2" gutterBottom>
                <strong>Product:</strong> {selectedAdjustment.itemName}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Original System Count:</strong> {selectedAdjustment.systemCount}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Physical Count:</strong> {selectedAdjustment.physicalCount}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Variance:</strong> {selectedAdjustment.variance > 0 ? '+' : ''}{selectedAdjustment.variance}
              </Typography>
              {selectedAdjustment.lossValue > 0 && (
                <Typography variant="body2" color="error.main" gutterBottom>
                  <strong>Loss Value:</strong> LKR {selectedAdjustment.lossValue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
                <strong>Reason:</strong> {selectedAdjustment.reason || '—'}
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                {selectedAdjustment.variance < 0 ? (
                  <>Stock will be restored and the associated expense record will be deleted.</>
                ) : (
                  <>The surplus stock will be removed from inventory.</>
                )}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReverseConfirmOpen(false)} disabled={reversing}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirmReverse}
            disabled={reversing}
            startIcon={reversing ? <CircularProgress size={18} color="inherit" /> : <Undo />}
          >
            {reversing ? 'Reversing...' : 'Confirm Reversal'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockAdjustment;
